/**
 * Error Transformation Utilities
 *
 * Transforms technical API errors into user-friendly, actionable messages.
 * Supports bilingual messages (FR/EN) and context-aware error handling.
 *
 * Invariant: TransformedError must never carry the raw axios error or its
 * config (Authorization JWT, request body) — enforced by
 * services/__tests__/axiosInstance.authStrip.test.ts.
 *
 * Invariant (DEV-7932): no support coordinates are written in this module. A message that
 * needs them receives a `SupportContact` through `ErrorContext.supportContact` and drops the
 * whole contact sentence when none is supplied. The functions here stay PURE — no hook, no
 * tenant store, no module-level state — because the axios interceptor calls them outside
 * React, where no such state could be read anyway. See `utils/supportContact`.
 */

import type { SupportContact } from './supportContact';

export interface TransformedError {
  /** User-friendly error message */
  message: {
    fr: string;
    en: string;
  };
  /** Error title (optional) */
  title?: {
    fr: string;
    en: string;
  };
  /** Unique error code for support */
  code: string;
  /** HTTP status code */
  status?: number;
  /** Recommended action */
  action: 'retry' | 'contact_support' | 'go_back' | 'refresh_page' | 'logout';
  /** Can this error be retried automatically? */
  retryable: boolean;
  /** Field-specific errors for form validation */
  fieldErrors?: Record<string, string>;
  /**
   * Business codes the BFF put in the error body's `codes` array (e.g. `CLAIM_ABANDONED`,
   * DEV-8836). Unrelated to {@link code}, which is a support tracking id generated here.
   * Only meaningful together with {@link status}: callers branch on the status first, then on
   * a code inside it. Absent when the body carried none.
   */
  businessCodes?: string[];
}

export interface ErrorContext {
  /** Action being performed when error occurred */
  action?: string;
  /** Current claim ID */
  claimId?: string;
  /** Current screen code */
  screenCode?: string;
  /**
   * Coordinates the CALLER is allowed to publish, from `resolveSupportContact(tenantConfig)`
   * (DEV-7932). Absent or `null` means the message carries no contact sentence at all — it is
   * never a cue to name a default number here, which is exactly what the invalid-file sentence
   * used to do: it spelled one insurer's travel-claims line into both `fr` and `en`, for every
   * tenant, including the corporate clients served through the shared generic portal shell. The
   * digits are deliberately not repeated in this comment either; `utils/supportContact` is the
   * only file in `src/` where they belong.
   *
   * Absent is the DELIBERATE default, and the consequence is accepted rather than worked
   * around: `services/api/axiosInstance` transforms errors inside an interceptor, outside
   * React, so it has no `useEmbeddedApp()` to resolve the tenant from and supplies nothing —
   * an error surfaced only through the interceptor therefore shows no number even to the
   * insurer entitled to it. That fails closed (a support call), where a resolver read from
   * module scope or a cached global would fail open (another insurer's members dialing a
   * claims department that cannot open their file) and would also break the purity this
   * module's callers rely on. React call sites that DO display the message pass the resolved
   * value — `screens/BankingInfoScreen` does, on the void-cheque upload.
   */
  supportContact?: SupportContact | null;
  /** Additional context */
  [key: string]: any;
}

/**
 * Appends the "if the problem persists, call…" sentence to a bilingual message, or returns the
 * base message untouched when this tenant publishes no coordinates.
 *
 * The base sentences are written to stand ALONE (DEV-7932): dropping the contact clause must
 * not leave "contactez le service à la clientèle au" hanging, so the fallback is a complete
 * alternative instruction rather than the same sentence with the digits cut out.
 */
const withSupportSentence = (
  base: { fr: string; en: string },
  supportContact: SupportContact | null | undefined
): { fr: string; en: string } =>
  supportContact
    ? {
        fr: `${base.fr} Si le problème persiste, contactez le service à la clientèle au ${supportContact.phone} ${supportContact.option}.`,
        en: `${base.en} If the problem persists, contact customer service at ${supportContact.phone} ${supportContact.option}.`,
      }
    : base;

/**
 * Transform an error into a user-friendly format
 */
/**
 * Detect if error is a WAF (Web Application Firewall) block
 * WAF returns HTML response with status 403
 */
export function isWAFError(error: any): { isWAF: boolean; trackingId?: string } {
  if (error.response?.status === 403) {
    const contentType = error.response.headers?.['content-type'] || '';
    const data = error.response.data;

    // WAF returns HTML, not JSON
    if (contentType.includes('text/html') || (typeof data === 'string' && data.includes('<html>'))) {
      // Extract WAF tracking ID from HTML response
      // Format: <p class="readable-font">20260121T184042Z-er1bdd6cc9ftwbl6hC1YMQenrs0000000aag0000000016ze</p>
      const trackingIdMatch = data.match(/readable-font[^>]*>([^<]+)</);
      const trackingId = trackingIdMatch ? trackingIdMatch[1].trim() : undefined;

      return { isWAF: true, trackingId };
    }
  }
  return { isWAF: false };
}

/**
 * Business codes from the BFF error envelope (`{ success, messages, codes: [...] }`), read
 * defensively: anything that is not an array of strings yields an empty list.
 */
export function readBusinessCodes(error: unknown): string[] {
  const codes = (error as { response?: { data?: { codes?: unknown } } } | null | undefined)
    ?.response?.data?.codes;
  return Array.isArray(codes) ? codes.filter((code): code is string => typeof code === 'string') : [];
}

export function transformError(
  error: unknown,
  context?: ErrorContext
): TransformedError {
  const transformed = transformErrorByStatus(error, context);
  const businessCodes = readBusinessCodes(error);
  return businessCodes.length > 0 ? { ...transformed, businessCodes } : transformed;
}

function transformErrorByStatus(
  error: any,
  context?: ErrorContext
): TransformedError {
  const errorCode = generateErrorCode();

  // Check for WAF block first (before other error types)
  const wafCheck = isWAFError(error);
  if (wafCheck.isWAF) {
    // WAF logging is handled by axios interceptor
    return {
      title: {
        fr: 'Requête bloquée',
        en: 'Request Blocked',
      },
      message: {
        fr: `Votre requête a été bloquée par le pare-feu de sécurité. Si le problème persiste, contactez le support avec le code: ${wafCheck.trackingId || errorCode}`,
        en: `Your request was blocked by the security firewall. If the problem persists, contact support with code: ${wafCheck.trackingId || errorCode}`,
      },
      code: errorCode,
      status: 403,
      action: 'contact_support',
      retryable: false,
    };
  }

  // Network error (no response)
  if (error.code === 'ERR_NETWORK' || !error.response) {
    // Special handling for critical listing endpoints
    if (context?.url?.includes('/claims/drafts') || context?.url?.includes('/claims/active')) {
      return {
        title: {
          fr: 'Service indisponible',
          en: 'Service Unavailable',
        },
        message: {
          fr: 'Impossible de récupérer vos réclamations. Le service pourrait être temporairement indisponible. Nous pourrions ne pas être en mesure de soumettre votre réclamation pour le moment.',
          en: 'Unable to retrieve your claims. The service may be temporarily unavailable. We may not be able to submit your claim at this time.',
        },
        code: errorCode,
        action: 'retry',
        retryable: true,
      };
    }

    // Translate action description if provided
    const actionDescriptionFr = context?.action ? getActionDescription(context.action, 'fr') : null;
    const actionDescriptionEn = context?.action ? getActionDescription(context.action, 'en') : null;

    return {
      title: {
        fr: 'Problème de connexion',
        en: 'Connection Problem',
      },
      message: {
        fr: actionDescriptionFr
          ? `Problème de connexion lors de ${actionDescriptionFr}. Vérifiez votre connexion internet et réessayez.`
          : 'Problème de connexion réseau. Vérifiez votre connexion internet et réessayez.',
        en: actionDescriptionEn
          ? `Connection issue while ${actionDescriptionEn}. Please check your internet connection and try again.`
          : 'Network connection issue. Please check your internet connection and try again.',
      },
      code: errorCode,
      action: 'retry',
      retryable: true,
    };
  }

  // Timeout error
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    // Translate action description if provided
    const actionDescriptionFr = context?.action ? getActionDescription(context.action, 'fr') : null;
    const actionDescriptionEn = context?.action ? getActionDescription(context.action, 'en') : null;

    return {
      title: {
        fr: 'Délai d\'attente dépassé',
        en: 'Request Timeout',
      },
      message: {
        fr: actionDescriptionFr
          ? `La requête a expiré lors de ${actionDescriptionFr}. Veuillez réessayer.`
          : 'La requête a pris trop de temps. Veuillez réessayer.',
        en: actionDescriptionEn
          ? `Request timed out while ${actionDescriptionEn}. Please try again.`
          : 'The request took too long. Please try again.',
      },
      code: errorCode,
      status: 408,
      action: 'retry',
      retryable: true,
    };
  }

  const status = error.response?.status || error.status;
  const errorData = error.response?.data || error;

  // Map status codes to user-friendly messages
  switch (status) {
    case 400:
      return handle400Error(errorData, errorCode, context);

    case 401:
      return {
        title: {
          fr: 'Session expirée',
          en: 'Session Expired',
        },
        message: {
          fr: 'Votre session a expiré. Veuillez vous reconnecter.',
          en: 'Your session has expired. Please log in again.',
        },
        code: errorCode,
        status: 401,
        action: 'logout',
        retryable: false,
      };

    case 403:
      return {
        title: {
          fr: 'Accès refusé',
          en: 'Access Denied',
        },
        message: {
          fr: 'Vous n\'avez pas les permissions nécessaires pour effectuer cette action.',
          en: 'You do not have permission to perform this action.',
        },
        code: errorCode,
        status: 403,
        action: 'go_back',
        retryable: false,
      };

    case 404:
      // Translate action description if provided
      const actionDescription404Fr = context?.action ? getActionDescription(context.action, 'fr') : null;
      const actionDescription404En = context?.action ? getActionDescription(context.action, 'en') : null;

      return {
        title: {
          fr: 'Non trouvé',
          en: 'Not Found',
        },
        message: {
          fr: actionDescription404Fr
            ? `La ressource demandée n'a pas été trouvée lors de ${actionDescription404Fr}.`
            : 'La réclamation ou ressource demandée n\'existe pas.',
          en: actionDescription404En
            ? `The requested resource was not found while ${actionDescription404En}.`
            : 'The claim or resource you requested does not exist.',
        },
        code: errorCode,
        status: 404,
        action: 'go_back',
        retryable: false,
      };

    case 409:
      return {
        title: {
          fr: 'Conflit de données',
          en: 'Data Conflict',
        },
        message: {
          fr: 'Les données ont été modifiées par une autre session. Veuillez recharger et réessayer.',
          en: 'The data has been modified by another session. Please reload and try again.',
        },
        code: errorCode,
        status: 409,
        action: 'refresh_page',
        retryable: false,
      };

    case 429:
      const retryAfter = error.response?.headers?.['retry-after'];
      return {
        title: {
          fr: 'Trop de requêtes',
          en: 'Too Many Requests',
        },
        message: {
          fr: retryAfter
            ? `Trop de requêtes. Veuillez patienter ${retryAfter} secondes avant de réessayer.`
            : 'Trop de requêtes. Veuillez patienter quelques instants.',
          en: retryAfter
            ? `Too many requests. Please wait ${retryAfter} seconds before trying again.`
            : 'Too many requests. Please wait a moment.',
        },
        code: errorCode,
        status: 429,
        action: 'retry',
        retryable: true,
      };

    case 500:
    case 502:
    case 503:
      // Translate action description if provided
      const actionDescription500Fr = context?.action ? getActionDescription(context.action, 'fr') : null;
      const actionDescription500En = context?.action ? getActionDescription(context.action, 'en') : null;

      return {
        title: {
          fr: 'Erreur serveur',
          en: 'Server Error',
        },
        message: {
          fr: actionDescription500Fr
            ? `Erreur serveur temporaire lors de ${actionDescription500Fr}. Veuillez réessayer dans quelques instants.`
            : 'Le serveur rencontre des difficultés temporaires. Veuillez réessayer dans quelques instants.',
          en: actionDescription500En
            ? `Temporary server error while ${actionDescription500En}. Please try again in a moment.`
            : 'The server is experiencing temporary difficulties. Please try again in a moment.',
        },
        code: errorCode,
        status,
        action: 'retry',
        retryable: true,
      };

    case 504:
      return {
        title: {
          fr: 'Délai d\'attente du serveur',
          en: 'Server Timeout',
        },
        message: {
          fr: 'Le serveur met trop de temps à répondre. Veuillez réessayer.',
          en: 'The server is taking too long to respond. Please try again.',
        },
        code: errorCode,
        status: 504,
        action: 'retry',
        retryable: true,
      };

    default:
      // Translate action description if provided
      const actionDescriptionDefaultFr = context?.action ? getActionDescription(context.action, 'fr') : null;
      const actionDescriptionDefaultEn = context?.action ? getActionDescription(context.action, 'en') : null;

      return {
        title: {
          fr: 'Erreur inattendue',
          en: 'Unexpected Error',
        },
        message: {
          fr: actionDescriptionDefaultFr
            ? `Une erreur inattendue s'est produite lors de ${actionDescriptionDefaultFr}. Veuillez réessayer ou contacter le support.`
            : 'Une erreur inattendue s\'est produite. Veuillez réessayer ou contacter le support.',
          en: actionDescriptionDefaultEn
            ? `An unexpected error occurred while ${actionDescriptionDefaultEn}. Please try again or contact support.`
            : 'An unexpected error occurred. Please try again or contact support.',
        },
        code: errorCode,
        status,
        action: 'contact_support',
        retryable: false,
      };
  }
}

/**
 * Handle 400 Bad Request errors (validation errors)
 */
function handle400Error(
  errorData: any,
  errorCode: string,
  context?: ErrorContext
): TransformedError {
  // Extract error messages from backend response (supports multiple formats)
  const errorMessages = errorData.messages?.errors || [];
  const errorMessage = errorMessages[0] || errorData.message || '';

  // Check if it's a file upload validation error
  const isFileError =
    errorMessage.toLowerCase().includes('file type') ||
    errorMessage.toLowerCase().includes('mime type') ||
    errorMessage.toLowerCase().includes('file not allowed') ||
    errorMessage.toLowerCase().includes('file size') ||
    context?.action === 'uploading_document';

  if (isFileError) {
    return {
      title: {
        fr: 'Fichier invalide',
        en: 'Invalid File',
      },
      message: withSupportSentence(
        {
          fr: 'Un problème a été détecté avec votre fichier. Veuillez essayer avec un autre fichier, ou avec une autre version ou un autre format du même fichier.',
          en: 'A problem was detected with your file. Please try another file, or a different version or format of the same file.',
        },
        context?.supportContact
      ),
      code: errorCode,
      status: 400,
      action: 'retry',
      retryable: true,
    };
  }

  // Check if it's a validation error with field-specific errors
  if (errorData.errors && typeof errorData.errors === 'object') {
    const fieldErrors: Record<string, string> = {};

    // Extract field errors
    Object.entries(errorData.errors).forEach(([field, message]) => {
      fieldErrors[field] = String(message);
    });

    return {
      title: {
        fr: 'Données invalides',
        en: 'Invalid Data',
      },
      message: {
        fr: 'Veuillez corriger les erreurs dans le formulaire.',
        en: 'Please correct the errors in the form.',
      },
      code: errorCode,
      status: 400,
      action: 'go_back',
      retryable: false,
      fieldErrors,
    };
  }

  // Generic 400 error
  // Translate action description if provided
  const actionDescription400Fr = context?.action ? getActionDescription(context.action, 'fr') : null;
  const actionDescription400En = context?.action ? getActionDescription(context.action, 'en') : null;

  return {
    title: {
      fr: 'Requête invalide',
      en: 'Invalid Request',
    },
    message: {
      fr: actionDescription400Fr
        ? `Les données fournies lors de ${actionDescription400Fr} sont invalides. Veuillez vérifier et réessayer.`
        : 'Les données fournies sont invalides. Veuillez vérifier les champs et réessayer.',
      en: actionDescription400En
        ? `The data provided while ${actionDescription400En} is invalid. Please check and try again.`
        : 'The data provided is invalid. Please check the fields and try again.',
    },
    code: errorCode,
    status: 400,
    action: 'go_back',
    retryable: false,
  };
}

/**
 * Generate a unique error code for support
 */
function generateErrorCode(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `ERR-${timestamp}-${random}`;
}

/**
 * Retry policy (DEV-7051): the FE retries only when the edge could not
 * respond at all — ie. transport-layer failure with no HTTP status.
 * Any HTTP response (4xx, 5xx, 429) is the BFF's authoritative answer
 * and surfaces immediately. The BFF already owns retries to upstream
 * services via WebClientRetrySpecs.
 */
export function shouldRetry(error: any, attemptNumber: number): boolean {
  if (attemptNumber >= 1) {
    return false;
  }

  if (error.code === 'ERR_NETWORK' || !error.response) {
    return true;
  }

  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return true;
  }

  return false;
}

/**
 * Calculate exponential backoff delay
 */
export function calculateBackoff(attemptNumber: number): number {
  // Exponential backoff: 1s, 2s, 4s
  return Math.pow(2, attemptNumber - 1) * 1000;
}


/**
 * Get context-aware action description for error messages
 */
export function getActionDescription(
  action: string,
  language: 'fr' | 'en' = 'fr'
): string {
  const actions: Record<string, { fr: string; en: string }> = {
    saving_draft: {
      fr: 'l\'enregistrement de votre brouillon',
      en: 'saving your draft',
    },
    submitting_claim: {
      fr: 'la soumission de votre réclamation',
      en: 'submitting your claim',
    },
    uploading_document: {
      fr: 'le téléchargement du document',
      en: 'uploading the document',
    },
    loading_claim: {
      fr: 'le chargement de votre réclamation',
      en: 'loading your claim',
    },
    validating_bank: {
      fr: 'la validation de vos informations bancaires',
      en: 'validating your banking information',
    },
    checking_eligibility: {
      fr: 'la vérification de votre admissibilité',
      en: 'checking your eligibility',
    },
    fetching_rates: {
      fr: 'la récupération des taux de change',
      en: 'fetching exchange rates',
    },
  };

  return actions[action]?.[language] || action;
}
